import { useEffect, useId, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { NavLink } from 'react-router-dom';
import { DSIcon, DSTypography } from '@eiv2/design-system';
import { NAV_LINKS } from './navConfig';

const SIDEBAR_EXPANDED_KEY = 'ei-cart-tracking.sidebar-expanded';

function readInitialExpanded(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    const stored = window.localStorage.getItem(SIDEBAR_EXPANDED_KEY);
    if (stored === null) return true;
    return stored !== 'false';
  } catch {
    return true;
  }
}

function SidebarTitle({ collapsed }: { collapsed?: boolean }) {
  if (collapsed) return null;

  return (
    <div className="flex w-full shrink-0 flex-col items-start overflow-hidden py-5">
      <div className="flex w-full items-center gap-3 overflow-hidden px-5">
        <div className="flex min-w-0 flex-1 flex-col gap-1 overflow-hidden">
          <DSTypography as="p" className="truncate text-[16px] font-semibold text-dark-3">
            Painel
          </DSTypography>
        </div>
      </div>
    </div>
  );
}

function CollapsedNavTooltip({ label, left, top, id }: { label: string; left: number; top: number; id: string }) {
  if (typeof document === 'undefined') return null;

  return createPortal(
    <span
      className="pointer-events-none fixed z-[70] -translate-y-1/2 whitespace-nowrap rounded bg-brand-primary-3 px-3 py-1 text-[14px] font-normal leading-normal text-body-light-mode-1 shadow-[0_8px_24px_rgba(0,0,0,0.08)]"
      id={id}
      role="tooltip"
      style={{ left, top }}
    >
      {label}
    </span>,
    document.body
  );
}

export function NavItems({
  collapsed = false,
  onNavigate
}: {
  collapsed?: boolean;
  onNavigate?: () => void;
}) {
  const tooltipId = useId();
  const [tooltip, setTooltip] = useState<{
    path: string;
    label: string;
    left: number;
    top: number;
  } | null>(null);

  useEffect(() => {
    if (!collapsed) setTooltip(null);
  }, [collapsed]);

  function showTooltip(path: string, label: string, target: HTMLElement) {
    if (!collapsed) return;
    const rect = target.getBoundingClientRect();
    setTooltip({
      path,
      label,
      left: rect.right + 10,
      top: rect.top + rect.height / 2
    });
  }

  function hideTooltip() {
    setTooltip(null);
  }

  return (
    <nav
      aria-label="Principal"
      className={`flex w-full flex-col gap-1 ${collapsed ? 'items-center px-2' : ''}`}
    >
      {NAV_LINKS.map((link) => (
        <NavLink
          key={link.path}
          to={link.path}
          aria-describedby={collapsed && tooltip?.path === link.path ? tooltipId : undefined}
          aria-label={collapsed ? link.label : undefined}
          onBlur={hideTooltip}
          onClick={() => {
            hideTooltip();
            onNavigate?.();
          }}
          onFocus={(event) => showTooltip(link.path, link.label, event.currentTarget)}
          onMouseEnter={(event) => showTooltip(link.path, link.label, event.currentTarget)}
          onMouseLeave={hideTooltip}
          className={({ isActive }) =>
            [
              'flex h-10 items-center gap-3 rounded-lg text-[14px] transition-colors duration-150 ease-out',
              collapsed ? 'w-11 justify-center px-0' : 'w-full',
              isActive
                ? collapsed
                  ? 'bg-brand-primary-3/10 font-semibold text-brand-primary-3'
                  : 'border-l-4 border-brand-primary-3 bg-brand-primary-3/10 px-3 py-2 font-semibold text-brand-primary-3'
                : collapsed
                  ? 'font-normal text-dark-3 hover:bg-gray-2'
                  : 'py-2 pr-3 pl-4 font-normal text-dark-3 hover:bg-gray-2'
            ].join(' ')
          }
        >
          {({ isActive }) => (
            <>
              <DSIcon
                className={isActive ? 'text-brand-primary-3' : 'text-dark-3'}
                name={link.icon}
                size={24}
              />
              {!collapsed ? <span className="min-w-0 flex-1 truncate">{link.label}</span> : null}
            </>
          )}
        </NavLink>
      ))}

      {collapsed && tooltip ? (
        <CollapsedNavTooltip
          id={tooltipId}
          label={tooltip.label}
          left={tooltip.left}
          top={tooltip.top}
        />
      ) : null}
    </nav>
  );
}

export function SidebarShell({
  children,
  collapsed = false,
  footer
}: {
  children: ReactNode;
  collapsed?: boolean;
  footer?: ReactNode;
}) {
  return (
    <div
      className={[
        'flex h-full flex-col overflow-hidden rounded-[24px] border border-solid border-gray-3 bg-body-light-mode-1 transition-[width] duration-200 ease-out',
        collapsed ? 'w-[88px]' : 'w-[280px]'
      ].join(' ')}
    >
      <SidebarTitle collapsed={collapsed} />
      <div className={`min-h-0 flex-1 overflow-x-clip overflow-y-auto pb-6 ${collapsed ? 'pt-5' : ''}`}>
        {children}
      </div>
      {footer}
    </div>
  );
}

export function Sidebar() {
  const [expanded, setExpanded] = useState(readInitialExpanded);

  useEffect(() => {
    try {
      window.localStorage.setItem(SIDEBAR_EXPANDED_KEY, String(expanded));
    } catch {
      /* ignore quota / privacy errors */
    }
  }, [expanded]);

  const collapsed = !expanded;

  return (
    <aside className="sticky top-16 hidden h-[calc(100dvh-4rem)] shrink-0 p-4 md:block">
      <SidebarShell
        collapsed={collapsed}
        footer={
          <div
            className={[
              'flex h-[72px] shrink-0 items-center px-4',
              collapsed ? 'justify-center' : 'justify-end'
            ].join(' ')}
          >
            <button
              aria-expanded={expanded}
              aria-label={expanded ? 'Recolher menu' : 'Expandir menu'}
              className="inline-flex h-10 w-11 cursor-pointer items-center justify-center rounded-[12px] text-dark-3 transition-colors duration-150 hover:bg-gray-2"
              onClick={() => setExpanded((current) => !current)}
              type="button"
            >
              <DSIcon name="HamburgerMenu" size={20} />
            </button>
          </div>
        }
      >
        <NavItems collapsed={collapsed} />
      </SidebarShell>
    </aside>
  );
}
